import { useCallback, useEffect, useMemo, useState } from "react"
import { useAxios } from "./AxiosContext"
import { Temporal } from "@js-temporal/polyfill"
import { Button, ButtonGroup, Form, Modal, Spinner, ToggleButton } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowsRotate, faBan, faBellSlash, faCheck, faEye, faEyeSlash, faPenToSquare } from "@fortawesome/free-solid-svg-icons"
import classNames from "classnames"
import styles from './SpendingLog.module.css'
import { AmountComponent } from "./AmountComponent"
import { Iban } from "./model/Iban"


interface Amount {
  amount: number
  currency: string
}

interface TransactionDate {
  booking: Temporal.PlainDate
  value: Temporal.PlainDate
}

interface Transaction {
  id: string
  amount: Amount
  title?: string
  entity?: string
  date: TransactionDate
}

interface TransactionWithMetadata {
  transaction: Transaction
  metadata: TransactionMetadata | null
}

type TransactionMetadata = TransactionMetadataValues.Irrelevant | TransactionMetadataValues.Data

namespace TransactionMetadataValues { // lint complains about having two types with the same name, even though it works
  export interface Irrelevant {
    irrelevant: {}
  }
  export interface Data {
    data: {
      description: string
    }
  }
}

interface TransactionWithMetadataAndIban extends TransactionWithMetadata {
  iban: Iban
}

export const SpendingLog: React.FC = () => {
  const axios = useAxios()

  const dateRanges = [{
    label: "Current month",
    value: "current-month"
  }, {
    label: "Last month",
    value: "last-month"
  }]

  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [transactions, setTransactions] = useState<Record<Iban, TransactionWithMetadata[]>>({})
  const [selectedDateRange, setSelectedDateRange] = useState(dateRanges[0])
  const [loadingCounter, setLoadingCounter] = useState(0)

  const flattenedTransactions: TransactionWithMetadataAndIban[] =
    Object.entries(transactions).flatMap(([iban, transactions]) => transactions.map(t => ({ ...t, iban }))).sort((a, b) => {
      const dateA = a.transaction.date.value || a.transaction.date.booking
      const dateB = b.transaction.date.value || b.transaction.date.booking

      return (dateA < dateB) ? 1 : ((dateA > dateB) ? -1 : 0)
    })

  const to = useMemo(() => {
    if (selectedDateRange.value === "current-month") {
      return Temporal.Now.plainDateISO()
    } else {
      return Temporal.Now.plainDateISO().with({day: 1}).subtract({ days: 1 })
    }
  }, [selectedDateRange])
  
  const from = useMemo(() => {
    if (selectedDateRange.value === "current-month") {
      return Temporal.Now.plainDateISO().with({day: 1})
    } else {
      return Temporal.Now.plainDateISO().with({day: 1}).subtract({ months: 1 })
    }
  }, [selectedDateRange])

  const [showIrrelevant, setShowIrrelevant] = useState(false)

  const refresh = async () => {
    setRefreshing(true)
    try {
      await axios.post("/v1/spending-log/refresh", undefined, {
        params: {
          from: from.toString(),
          to: to.toString()
        }
      })
      setLoadingCounter(loadingCounter + 1)
    } catch (error) {
      console.error("Error refreshing transactions", error)
    } finally {
      setRefreshing(false)
    }
  }

  const updateMetadata = async (transaction: Transaction, iban: Iban, metadata: TransactionMetadata) => {
    try {
      await axios.put("/v1/spending-log/metadata", {
        transactionId: transaction.id,
        metadata,
        iban,
        transactionDate: transaction.date.booking ? transaction.date.booking.toString() : transaction.date.value!.toString()
      })

      setTransactions(previous => {
        return Object.fromEntries(
          Object.entries(previous).map(([iban, transactionsWithMetadata]) => {
            const updatedTransactionsWithMetadata: TransactionWithMetadata[] =
              transactionsWithMetadata.map(transactionWithMetadata => {
                if (transactionWithMetadata.transaction.id === transaction.id) {
                  return { ...transactionWithMetadata, metadata }
                } else {
                  return transactionWithMetadata
                }
              })
            return [
              iban,
              updatedTransactionsWithMetadata
            ]
          }))
      })
    } catch (error) {
      console.error("Error updating transaction metadata", error)
    }
  }

  useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      try {
        const response = await axios.get("/v1/spending-log", {
          params: {
            from: from.toString(),
            to: to.toString()
          }
        })
        setTransactions(response.data.transactions)

      } catch (error) {
        console.error("Error fetching transactions", error)
      } finally {
        setLoading(false)
      }

    }
    console.log("Fetching...", from.toString(), to.toString())
    fetch()
  }, [axios, to, from, loadingCounter])

  return <div className="py-3">
    {
      (loading) ? <div className="text-center my-3"><Spinner animation="border" /></div> :
        <div>
          <h3>Transactions</h3>
          <div className="mb-2 d-flex gap-1 justify-content-end">
            <Button variant="secondary" onClick={refresh} disabled={refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} /> {refreshing ? "Refreshing..." : "Force Refresh"}
            </Button>
            <ToggleButton id="spending-log-show-irrelevant" value="1" type="checkbox" variant="secondary" checked={showIrrelevant} onChange={e => setShowIrrelevant(e.currentTarget.checked)}>{showIrrelevant ? <><FontAwesomeIcon icon={faEyeSlash} /> Hide irrelevant</> : <><FontAwesomeIcon icon={faEye} /> Show irrelevant</>}</ToggleButton>
            <ButtonGroup>
              {dateRanges.map((dateRange, idx) => (
                <ToggleButton
                  key={idx}
                  id={`transactions-time-option-${idx}`}
                  type="radio"
                  variant="secondary"
                  name="transaction-time-option"
                  value={dateRange.value}
                  checked={dateRange.value === selectedDateRange.value}
                  onChange={() => setSelectedDateRange(dateRanges[idx])}
                >
                  {dateRange.label}
                </ToggleButton>
              ))}
            </ButtonGroup>
          </div>

          <table className={classNames({ table: true, "table-striped": true, [styles.spendingLog]: true })}>
            <thead>
              <tr>
                <th className={styles.date}>Date</th>
                <th></th>
                <th className={styles.title}>Title</th>
                <th className={styles.amount}>Amount</th>
                <th className={styles.actions}>Actions</th>
              </tr>
            </thead>
            <tbody>

              {
                (flattenedTransactions.length === 0) ? <tr><td colSpan={6} className="text-center">No transactions</td></tr> :
                  flattenedTransactions.filter(({ metadata }) => showIrrelevant || !(metadata && "irrelevant" in metadata))
                    .map(({ transaction, metadata, iban }) =>
                      <TransactionComponent key={transaction.id} transaction={transaction} metadata={metadata} onMetadataUpdate={metadata => updateMetadata(transaction, iban, metadata)} />
                    )
              }
            </tbody>
          </table>
        </div>
    }
  </div>
}

const TransactionComponent: React.FC<{ transaction: Transaction, metadata: TransactionMetadata | null, onMetadataUpdate: ((metadata: TransactionMetadata) => Promise<void>) }> = ({ transaction, metadata, onMetadataUpdate }) => {
  const [showModal, setShowModal] = useState(false)
  const [description, setDescription] = useState<string>("")
  const [saving, setSaving] = useState(false)

  const markAsIrrelevant = async () => {
    try {
      setSaving(true)
      await onMetadataUpdate({
        irrelevant: {}
      })
    } catch (error) {
      console.error("Error marking transaction as irrelevant", error)
    } finally {
      setSaving(false)
    }

  }

  const saveDescription = useCallback(async () => {
    try {
      setSaving(true)
      await onMetadataUpdate(
        {
          data: {
            description: description.trim()
          }
        }
      )
      setShowModal(false)
    } catch (error) {
      console.error("Error saving transaction description", error)
    } finally {
      setSaving(false)
    }

  }, [onMetadataUpdate, description])

  return <tr key={transaction.id} className={classNames({ [styles.irrelevant]: metadata && ("irrelevant" in metadata) })}>
    <td className={styles.date}>
      <span>{transaction.date.value ? transaction.date.value.toString() : transaction.date.booking ? transaction.date.booking.toString() : "-"}</span>

    </td>
    <td className={classNames({ [styles.green]: metadata && "data" in metadata, [styles.gray]: metadata && "irrelevant" in metadata, [styles.red]: !metadata })}>&#9679;</td>
    <td className={styles.title}>
      <p className={styles.originalData}>
        <span>{transaction.entity}</span>
        {transaction.entity && transaction.title && metadata && <span>&nbsp;&bull;&nbsp;</span>}
        {metadata && <span className={classNames({ [styles.originalTitle]: (metadata && "data" in metadata) })}>{transaction.title}</span>}
      </p>
      <p>{(metadata && "data" in metadata) ? <> {metadata.data.description}</> : <>{transaction.title}</>}</p>
    </td>
    <td className={classNames(["text-end", styles.amount])}><AmountComponent amount={transaction.amount.amount} currency={transaction.amount.currency} /></td>
    <td className={styles.actions}>
      <Modal show={showModal}>
        <Modal.Header closeButton onHide={() => setShowModal(false)}>
          <Modal.Title>Transaction details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={e => { e.preventDefault(); saveDescription() }}>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control type="textarea" autoFocus defaultValue={(metadata && ("data" in metadata)) ? metadata.data.description : ""} onChange={(e) => setDescription(e.currentTarget.value)} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div className="mt-3 d-flex gap-1">
            <Button variant="secondary" onClick={() => setShowModal(false)}><FontAwesomeIcon icon={faBan} /> Cancel</Button>
            <Button variant="primary" disabled={description.trim().length < 3} onClick={() => saveDescription()}><FontAwesomeIcon icon={faCheck} /> Save</Button>
          </div>
        </Modal.Footer>
      </Modal>
      <div className="d-flex gap-1">
        {!(metadata && "irrelevant" in metadata) && <Button variant="secondary" onClick={() => markAsIrrelevant()} disabled={saving}><FontAwesomeIcon icon={faBellSlash} /> Irrelevant</Button>}
        {!(metadata && "data" in metadata) && <Button variant="primary" onClick={() => setShowModal(true)} disabled={saving}><FontAwesomeIcon icon={faPenToSquare} /> Describe</Button>}
      </div>


    </td>
  </tr>
}
